import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { serverFetch } from "../../server";
import { Button, Checkbox, Col, Form, Input, message, Row, Select, Space } from "antd";
import TakePhoto from "../../controls/take-photo/take-photo";
import UploadPhoto from "../../controls/take-photo/upload-photo";
import "./company.css";
import { LogMonitor } from "@/controls";
import { useSelector } from "react-redux";
import { isMobile, menuSize } from "@/extensions/design";
import { recordPassVidOptions } from "@/extensions/utils";

const CHANNEL_VID_CHAT_SITE = 201;

const Company = forwardRef(({ onHeaderStateChange, onHeaderLoadingChange }, ref) => {
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState();
  const [modified, setModified] = useState(false);
  const [form] = Form.useForm();
  const [takePhoto, setTakePhoto] = useState(false);
  const [uploadPhoto, setUploadPhoto] = useState(false);
  const [imageVersion, setImageVersion] = useState(Date.now());
  const [backgroundTakePhoto, setBackgroundTakePhoto] = useState(false);
  const [backgroundUploadPhoto, setBackgroundUploadPhoto] = useState(false);
  const [backgroundImageVersion, setBackgroundImageVersion] = useState(Date.now());
  const [bannerTakePhoto, setBannerTakePhoto] = useState(false);
  const [bannerUploadPhoto, setBannerUploadPhoto] = useState(false);
  const [bannerImageVersion, setBannerImageVersion] = useState(Date.now());
  const [mobileBannerTakePhoto, setMobileBannerTakePhoto] = useState(false);
  const [mobileBannerUploadPhoto, setMobileBannerUploadPhoto] = useState(false);
  const [mobileBannerImageVersion, setMobileBannerImageVersion] = useState(Date.now());
  const [pwaTakePhoto, setPwaTakePhoto] = useState(false);
  const [pwaUploadPhoto, setPwaUploadPhoto] = useState(false);
  const [pwaImageVersion, setPwaImageVersion] = useState(Date.now());
  const [chatChannels, setChatChannels] = useState([]);
  const [vidCardOptions, setVidCardOptions] = useState([]);
  const user = useSelector((s) => s.user);

  const onlineCreateCardOptions = [
    { label: "Не генерировать", value: null },
    { label: "Карта 3 байта", value: 6 },
    { label: "Карта 4 байта", value: 8 },
    { label: "Карта 5 байт", value: 10 }
  ];

  const formatGmt = (gmt) => {
    if (gmt === null || gmt === undefined) {
      return "";
    }

    return `GMT${gmt >= 0 ? "+" : ""}${gmt}`;
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "";
    }

    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (!match) {
      return String(value);
    }

    const [, year, month, day, hour, minute, second] = match;
    return `${day}.${month}.${year} ${hour}:${minute}:${second}`;
  };

  const notifyHeader = () => {
    onHeaderStateChange?.(modified);
    onHeaderLoadingChange?.(loading);
  };

  const onSavePwaPhoto = (file) => {
    const formData = new FormData();
    formData.append("file", file, file.name);
    serverFetch(`Company/pwaPhoto/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        setPwaImageVersion(Date.now());
        onClosePwaPhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onDeletePwaPhoto = () => {
    const formData = new FormData();
    serverFetch(`Company/pwaPhoto/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        setPwaImageVersion(Date.now());
        onClosePwaPhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onClosePwaPhoto = (refresh) => {
    setPwaTakePhoto(false);
    refresh && getData();
  };

  const onClosePwaUpload = (refresh) => {
    setPwaUploadPhoto(false);
    refresh && getData();
  };
  
  const getSaveData = () => {
    const saveData = { ...item };
    delete saveData.serverGmt;
    delete saveData.serverTime;
    delete saveData.companyTime;
    delete saveData.chatChannelCode;

    return saveData;
  };

  const getData = () => {
    setLoading(true);
    serverFetch('company/current')
      .then(data => {
        setItem(data);
        setLoading(false);
        setModified(false);
        form.setFieldsValue(data);
        notifyHeader();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  useEffect(getData, []);

  useEffect(() => {
    serverFetch("Message/Channel")
      .then((data) => {
        const options = (Array.isArray(data) ? data : [])
          .filter((ch) => ch.vid === CHANNEL_VID_CHAT_SITE)
          .map((ch) => ({
            value: ch.id,
            label: ch.name ? `${ch.name}${ch.active ? "" : " (неактивен)"}` : `#${ch.id}${ch.active ? "" : " (неактивен)"}`,
          }));
        setChatChannels(options);
      })
      .catch(() => setChatChannels([]));
  }, []);

  useEffect(() => {
    serverFetch("Card/vid")
      .then(data => {
        const options = (Array.isArray(data) ? data : []).map(vid => ({
          value: vid.id,
          label: vid.name,
        }));
        setVidCardOptions(options);
      })
      .catch(() => setVidCardOptions([]));
  }, []);

  const onValuesChange = (value) => {
    setModified(true);
    setItem({ ...item, ...value });
    onHeaderStateChange?.(true);
  };

  const onSave = () => {
    form.validateFields().then(() => {
      setLoading(true);
      serverFetch("company", { method: "PUT", bodyData: getSaveData()})
        .then(() => {
          setLoading(false);
          setModified(false);
          message.success("Сохранено");
          onHeaderStateChange?.(false);
        })
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        });
    });
  };

  useImperativeHandle(ref, () => ({
    save: onSave,
    isModified: () => modified,
  }));

  const onClosePhoto = (refresh) => {
    setTakePhoto(false);
    refresh && getData();
  }

  const onCloseUpload = (refresh) => {
    setUploadPhoto(false);
    refresh && getData();
  }

  const onSavePhoto = (file) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    serverFetch(`company/photo/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        const timestamp = Date.now();
        setItem((prev) => ({ ...prev, updatedAt: timestamp }));
        setImageVersion(Date.now());
        onClosePhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onDeletePhoto = () => {
    const formData = new FormData();
    serverFetch(`company/photo/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        const timestamp = Date.now();
        setItem((prev) => ({ ...prev, updatedAt: timestamp }));
        setImageVersion(Date.now());
        onClosePhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onCloseBackgroundPhoto = (refresh) => {
    setBackgroundTakePhoto(false);
    refresh && getData();
  };

  const onCloseBackgroundUpload = (refresh) => {
    setBackgroundUploadPhoto(false);
    refresh && getData();
  };

  const onSaveBackgroundPhoto = (file) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    serverFetch(`Company/backgroundPhoto/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        setBackgroundImageVersion(Date.now());
        onCloseBackgroundPhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onDeleteBackgroundPhoto = () => {
    const formData = new FormData();
    serverFetch(`Company/backgroundPhoto/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        setBackgroundImageVersion(Date.now());
        onCloseBackgroundPhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onCloseBannerPhoto = (refresh) => {
    setBannerTakePhoto(false);
    refresh && getData();
  };

  const onCloseBannerUpload = (refresh) => {
    setBannerUploadPhoto(false);
    refresh && getData();
  };
  const validateMobileBannerFile = (file) => {
    return new Promise((resolve) => {
      const image = new Image();
      const url = URL.createObjectURL(file);

      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(true);
      };

      image.onerror = () => {
        URL.revokeObjectURL(url);
        message.error('Не удалось прочитать изображение');
        resolve(false);
      };

      image.src = url;
    });
  };

  const onCloseMobileBannerPhoto = (refresh) => {
    setMobileBannerTakePhoto(false);
    refresh && getData();
  };

  const onCloseMobileBannerUpload = (refresh) => {
    setMobileBannerUploadPhoto(false);
    refresh && getData();
  };

  const onSaveMobileBannerPhoto = async (file) => {
    const isValid = await validateMobileBannerFile(file);
    if (!isValid) {
      return false;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);
    try {
      await serverFetch(`Company/bannerMobilePhoto/${item.id}`, { method: "PUT", body: formData });
      setMobileBannerImageVersion(Date.now());
      return true;
    } catch (e) {
      message.error(e.userMessage);
      return false;
    }
  };

  const onDeleteMobileBannerPhoto = () => {
    const formData = new FormData();
    serverFetch(`Company/bannerMobilePhoto/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        setMobileBannerImageVersion(Date.now());
        onCloseMobileBannerPhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };


  const onSaveBannerPhoto = (file) => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    serverFetch(`Company/bannerPhoto/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        setBannerImageVersion(Date.now());
        onCloseBannerPhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  const onDeleteBannerPhoto = () => {
    const formData = new FormData();
    serverFetch(`Company/bannerPhoto/${item.id}`, { method: "PUT", body: formData })
      .then(() => {
        setBannerImageVersion(Date.now());
        onCloseBannerPhoto(true);
      })
      .catch(e => message.error(e.userMessage));
  };

  return !isMobile ? (<div>
    <Form form={form} onValuesChange={onValuesChange} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }} colon={false} disabled={loading}>
      <Form.Item label="Наименование" name="name"><Input /></Form.Item>
      <Form.Item label="Описание" name="description"><Input.TextArea count={5} style={{minHeight: 120}} autoSize/></Form.Item>
      <Form.Item label="Заголовок баннера" name="bannerTitle"><Input maxLength={200} /></Form.Item>
      <Form.Item label=" ">
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Телефоны" name="phone"><Input /></Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Почта" name="email"><Input /></Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Адрес" name="address"><Input /></Form.Item>
          </Col>
        </Row>
      </Form.Item>
      <Form.Item label=" ">
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Часовой пояс" name="gmt">
              <Input style={{ maxWidth: 120, textAlign: "right" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Начало работы (0..24)" name="hourStart">
              <Input style={{ maxWidth: 120, textAlign: "right" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Окончание работы (0..24)" name="hourFinish">
              <Input style={{ maxWidth: 120, textAlign: "right" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>
      <Form.Item label=" ">
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item label="Часовой пояс сервера">
              <Input value={formatGmt(item?.serverGmt)} style={{ maxWidth: 160, textAlign: "right" }} readOnly />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Время на сервере">
              <Input value={formatDateTime(item?.serverTime)} style={{ maxWidth: 180, textAlign: "right" }} readOnly />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Время компании">
              <Input value={formatDateTime(item?.companyTime)} style={{ maxWidth: 180, textAlign: "right" }} readOnly />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>
      <Form.Item label="Генерировать карту" name="onlineCreateCard">
        <Select options={onlineCreateCardOptions} allowClear placeholder="Не генерировать" />
      </Form.Item>
      <Form.Item
        label="Вид карт клиентов"
        name="vidCardClients"
        rules={[({ getFieldValue }) => ({
          validator(_, value) {
            return getFieldValue("onlineCreateCard") && !value
              ? Promise.reject(new Error("Выберите вид карт клиентов"))
              : Promise.resolve();
          },
        })]}
      >
        <Select options={vidCardOptions} allowClear showSearch optionFilterProp="label" placeholder="Не выбран" />
      </Form.Item>
      <Form.Item label="Канал чата" name="chatChannelId">
        <Select options={chatChannels} allowClear placeholder="Не выбран" />
      </Form.Item>
      <Form.Item label=" " name="recordProductClientCheck" valuePropName="checked"><Checkbox>Проверять наличие клиента и услуги в индивидуальной записи</Checkbox></Form.Item>
      <Form.Item label=" " name="closeVisitAfterPayment" valuePropName="checked"><Checkbox>Закрывать визит при оплате</Checkbox></Form.Item>
      <Form.Item label=" " name="closeDateInclusive" valuePropName="checked"><Checkbox>Добавлять день к сроку действия (месяц, год)</Checkbox></Form.Item>
      <Form.Item label="Вид прохода по записи" name="recordPassVid">
        <Select options={recordPassVidOptions} />
      </Form.Item>
      <Form.Item label="Описание сервиса (URL)" name="paymentConditionUrl"><Input maxLength={200} /></Form.Item>
      <Form.Item label="Условия Автоплатеж (URL)" name="autopayConditionUrl"><Input maxLength={200} /></Form.Item>
      <Form.Item label="Польз. соглашение (URL)" name="userAgreementUrl"><Input maxLength={200} /></Form.Item>
      <Form.Item label="Условия и правила (URL)" name="rulesUrl"><Input maxLength={200} /></Form.Item>
      <Form.Item label="Рекламный URL" name="adUrl"><Input maxLength={200} /></Form.Item>
      <Form.Item label=" ">
        <Space>
          <LogMonitor vid={9} id={user?.companyId}/>
          <Button disabled={!modified || loading} onClick={onSave}>Сохранить</Button>
        </Space>
      </Form.Item>
      <Form.Item label=" ">
        <Row gutter={16}>
          <Col span={5}>
            <div style={{ marginBottom: 8 }}>Логотип</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img
                src={item?.pictureId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.pictureId}.jpg?` : "/images/logo.svg"}
                alt="company"
                style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
              />
              {item?.id !== 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setTakePhoto(true)} />
                  <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setUploadPhoto(true)} />
                  {item?.backgroundImageId && (
                    <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => onDeletePhoto()} />
                  )}
                </div>
              )}
            </div>
            {takePhoto && <TakePhoto onClose={onClosePhoto} onSave={onSavePhoto} />}
            {uploadPhoto && <UploadPhoto onClose={onCloseUpload} onSave={onSavePhoto} />}
          </Col>
          <Col span={5}>
            <div style={{ marginBottom: 8 }}>Фон каталога</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img
                src={item?.backgroundImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.backgroundImageId}.jpg?v=${backgroundImageVersion}` : "/images/sample.png"}
                alt="background"
                style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
              />
              {item?.id !== 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBackgroundTakePhoto(true)} />
                  <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBackgroundUploadPhoto(true)} />
                  {item?.backgroundImageId && (
                    <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={onDeleteBackgroundPhoto} />
                  )}
                </div>
              )}
            </div>
            {backgroundTakePhoto && <TakePhoto onClose={onCloseBackgroundPhoto} onSave={onSaveBackgroundPhoto} />}
            {backgroundUploadPhoto && <UploadPhoto onClose={onCloseBackgroundUpload} onSave={onSaveBackgroundPhoto} />}
          </Col>
          <Col span={5}>
            <div style={{ marginBottom: 8 }}>Картинка баннера</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img
                src={item?.bannerImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.bannerImageId}.jpg?v=${bannerImageVersion}` : "/images/sample.png"}
                alt="banner"
                style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
              />
              {item?.id !== 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBannerTakePhoto(true)} />
                  <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBannerUploadPhoto(true)} />
                  {item?.bannerImageId && (
                    <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={onDeleteBannerPhoto} />
                  )}
                </div>
              )}
            </div>
            <div style={{marginTop: "4px"}}> Рекомендуемый размер: 1200х340 </div>
            {bannerTakePhoto && <TakePhoto onClose={onCloseBannerPhoto} onSave={onSaveBannerPhoto} />}
            {bannerUploadPhoto && <UploadPhoto onClose={onCloseBannerUpload} onSave={onSaveBannerPhoto} recommendedSize={{ width: 1200, height: 340 }} />}
          </Col>
          <Col span={5}>
            <div style={{ marginBottom: 8 }}>Моб. баннер</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img
                src={item?.mobileBannerImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.mobileBannerImageId}.jpg?v=${mobileBannerImageVersion}` : "/images/sample.png"}
                alt="mobile banner"
                style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
              />
              {item?.id !== 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setMobileBannerTakePhoto(true)} />
                  <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setMobileBannerUploadPhoto(true)} />
                  {item?.mobileBannerImageId && (
                    <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={onDeleteMobileBannerPhoto} />
                  )}
                </div>
              )}
            </div>
            <div style={{marginTop: "4px"}}> Рекомендуемый размер: 300х150 </div>
            {mobileBannerTakePhoto && <TakePhoto onClose={onCloseMobileBannerPhoto} onSave={onSaveMobileBannerPhoto}/>}
            {mobileBannerUploadPhoto && <UploadPhoto asyncSave onClose={onCloseMobileBannerUpload} onSave={onSaveMobileBannerPhoto} recommendedSize={{ width: 300, height: 150 }} />}
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8 }}>Иконка PWA</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img
                src={item?.pwaImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.pwaImageId}.jpg?v=${pwaImageVersion}` : "/images/sample.png"}
                alt="pwa"
                style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
              />
              {item?.id !== 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setPwaTakePhoto(true)} />
                  <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setPwaUploadPhoto(true)} />
                  {item?.pwaImageId && (
                    <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={onDeletePwaPhoto} />
                  )}
                </div>
              )}
            </div>
            {pwaTakePhoto && <TakePhoto onClose={onClosePwaPhoto} onSave={onSavePwaPhoto} />}
            {pwaUploadPhoto && <UploadPhoto onClose={onClosePwaUpload} onSave={onSavePwaPhoto} />}
          </Col>
        </Row>
      </Form.Item>
    </Form>
  </div>) : (
    <div className="stock-scrollable-list" style={{ height: window.innerHeight - menuSize - 30, width: "100%"}}>
    <Form form={form} style={{padding: "0px 6px 0px 6px"}} onValuesChange={onValuesChange} labelCol={{ xs: 4 }} wrapperCol={{ xs: 20 }} colon={false} disabled={loading} layout="inline">
      <Form.Item label="Наимен." name="name" className="settings-mobile-form-item">
        <Input className="records-form-item-mobile"/>
      </Form.Item>
      <Form.Item label="Описан." name="description" className="settings-mobile-form-item">
        <Input.TextArea count={5} style={{minHeight: 120}} autoSize/>
      </Form.Item>
      <Form.Item label="Заг.банн." name="bannerTitle" className="settings-mobile-form-item">
        <Input maxLength={200} />
      </Form.Item>
      <Form.Item label="Телеф-ы" name="phone" className="settings-mobile-form-item">
        <Input />
      </Form.Item>
      <Form.Item label="Почта" name="email" className="settings-mobile-form-item">
        <Input />
      </Form.Item>
      <Form.Item label="Адрес" name="address" className="settings-mobile-form-item">
        <Input />
      </Form.Item>
      <Form.Item label="Час.пояс" className="settings-mobile-form-item">
        <div style={{display: "flex", flexWrap: "nowrap", gap: 4, alignItems: "center"}}>
          <Form.Item name="gmt" noStyle>
            <Input style={{maxWidth: 120}}/>
          </Form.Item>
          <span style={{whiteSpace: "nowrap", fontSize: "12px"}}>Нач.раб.(0-24)</span>
          <Form.Item name="hourStart" noStyle>
            <Input style={{maxWidth: 120}}/>
          </Form.Item>
          <span style={{whiteSpace: "nowrap", fontSize: "12px"}}>Кон.раб.(0-24)</span>
          <Form.Item name="hourFinish" noStyle>
            <Input style={{maxWidth: 120}}/>
          </Form.Item>
        </div>
      </Form.Item>
      <Form.Item label="Время" className="settings-mobile-form-item">
        <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: "12px" }}>
          <span>Часовой пояс сервера: {formatGmt(item?.serverGmt)}</span>
          <span>Время на сервере: {formatDateTime(item?.serverTime)}</span>
          <span>Время компании: {formatDateTime(item?.companyTime)}</span>
        </div>
      </Form.Item>
      <Form.Item label="Ген.карт. по запр." name="onlineCreateCard" className="settings-mobile-form-item">
        <Select options={onlineCreateCardOptions} allowClear placeholder="Не генерировать" />
      </Form.Item>
      <Form.Item
        label="Вид карт клиентов"
        name="vidCardClients"
        className="settings-mobile-form-item"
        rules={[({ getFieldValue }) => ({
          validator(_, value) {
            return getFieldValue("onlineCreateCard") && !value
              ? Promise.reject(new Error("Выберите вид карт клиентов"))
              : Promise.resolve();
          },
        })]}
      >
        <Select options={vidCardOptions} allowClear showSearch optionFilterProp="label" placeholder="Не выбран" />
      </Form.Item>
      <Form.Item label="Канал чата" name="chatChannelId" className="settings-mobile-form-item">
        <Select options={chatChannels} allowClear placeholder="Не выбран" />
      </Form.Item>

      <Form.Item name="recordProductClientCheck" valuePropName="checked" className="settings-mobile-form-item">
        <Checkbox style={{whiteSpace: "nowrap", fontSize: "12px"}}>Проверять наличие клиента и усл. в индив. записи</Checkbox>
      </Form.Item>
      <Form.Item name="closeVisitAfterPayment" valuePropName="checked" className="settings-mobile-form-item">
        <Checkbox style={{whiteSpace: "nowrap", fontSize: "12px"}}>Закрывать визит при оплате</Checkbox>
      </Form.Item>
      <Form.Item name="closeDateInclusive" valuePropName="checked" className="settings-mobile-form-item">
        <Checkbox style={{whiteSpace: "nowrap", fontSize: "12px"}}>Добавлять день к сроку действия (месяц, год)</Checkbox>
      </Form.Item>
      
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label={<span style={{ fontSize: '11px' }}>Опис.(URL)</span>} name="paymentConditionUrl" className="settings-mobile-form-item"><Input/></Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label={<span style={{ fontSize: '11px' }}>Усл.автопл.(URL)</span>} name="autopayConditionUrl" className="settings-mobile-form-item"><Input/></Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label={<span style={{ fontSize: '11px' }}>Польз.согл.(URL)</span>} name="userAgreementUrl" className="settings-mobile-form-item"><Input/></Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label="Усл. (URL)" name="rulesUrl" className="settings-mobile-form-item"><Input/></Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label="Рекл. URL" name="adUrl" className="settings-mobile-form-item"><Input/></Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label="Логотип" className="settings-mobile-form-item">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {item && <img src={item.pictureId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.pictureId}.jpg` : "/images/logo.svg"} alt="company" style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }} />}
          {item?.id !== 0 && <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setTakePhoto(true)} />}
          {item?.id !== 0 && <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setUploadPhoto(true)} />}
          {item?.id !== 0 && <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => onDeletePhoto()} />}
        </div>
        {takePhoto && <TakePhoto onClose={onClosePhoto} onSave={onSavePhoto} />}
        {uploadPhoto && <UploadPhoto onClose={onCloseUpload} onSave={onSavePhoto} />}
      </Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label="Фон кат." className="settings-mobile-form-item">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={item?.backgroundImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.backgroundImageId}.jpg?v=${backgroundImageVersion}` : "/images/sample.png"}
            alt="background"
            style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
          />
          {item?.id !== 0 && (
            <>
              <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBackgroundTakePhoto(true)} />
              <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBackgroundUploadPhoto(true)} />
              {item?.backgroundImageId && (
                <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={onDeleteBackgroundPhoto} />
              )}
            </>
          )}
        </div>
        {backgroundTakePhoto && <TakePhoto onClose={onCloseBackgroundPhoto} onSave={onSaveBackgroundPhoto} />}
        {backgroundUploadPhoto && <UploadPhoto onClose={onCloseBackgroundUpload} onSave={onSaveBackgroundPhoto} />}
      </Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label="Баннер" className="settings-mobile-form-item">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={item?.bannerImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.bannerImageId}.jpg?v=${bannerImageVersion}` : "/images/sample.png"}
            alt="banner"
            style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
          />
          {item?.id !== 0 && (
            <>
              <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBannerTakePhoto(true)} />
              <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setBannerUploadPhoto(true)} />
              {item?.bannerImageId && (
                <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={onDeleteBannerPhoto} />
              )}
              <div>Рек. разм.: 1200х340</div>
            </>
          )}
        </div>
        {bannerTakePhoto && <TakePhoto onClose={onCloseBannerPhoto} onSave={onSaveBannerPhoto} />}
        {bannerUploadPhoto && <UploadPhoto onClose={onCloseBannerUpload} onSave={onSaveBannerPhoto} recommendedSize={{ width: 1200, height: 340 }} />}
      </Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label="Моб. баннер" className="settings-mobile-form-item">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src={item?.mobileBannerImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.mobileBannerImageId}.jpg?v=${mobileBannerImageVersion}` : "/images/sample.png"}
            alt="mobile banner"
            style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }}
          />
          {item?.id !== 0 && (
            <>
              <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setMobileBannerTakePhoto(true)} />
              <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setMobileBannerUploadPhoto(true)} />
              {item?.mobileBannerImageId && (
                <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={onDeleteMobileBannerPhoto} />
              )}
              <div>Рек. разм.: 300x150</div>
            </>
          )}
        </div>
        {mobileBannerTakePhoto && <TakePhoto onClose={onCloseMobileBannerPhoto} onSave={onSaveMobileBannerPhoto}/>}
        {mobileBannerUploadPhoto && <UploadPhoto asyncSave onClose={onCloseMobileBannerUpload} onSave={onSaveMobileBannerPhoto} recommendedSize={{ width: 300, height: 150 }} />}
      </Form.Item>
      <Form.Item labelCol={{ xs: 7 }} wrapperCol={{ xs: 17 }} label="Иконка PWA" className="settings-mobile-form-item">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {item && <img src={item.pwaImageId ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${item.pwaImageId}.jpg` : "/images/sample.png"} alt="company" style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4, border: "1px solid #f0f0f0" }} />}
          {item?.id !== 0 && <img src="/images/photo.svg" alt="take-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setPwaTakePhoto(true)} />}
          {item?.id !== 0 && <img src="/images/upload-photo.svg" alt="upload-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => setPwaUploadPhoto(true)} />}
          {item?.id !== 0 && <img src="/images/delete-button.svg" alt="delete-photo" style={{ width: 32, cursor: "pointer" }} onClick={() => onDeletePwaPhoto()} />}
        </div>
        {pwaTakePhoto && <TakePhoto onClose={onClosePwaPhoto} onSave={onSavePwaPhoto} />}
        {pwaUploadPhoto && <UploadPhoto onClose={onClosePwaUpload} onSave={onSavePwaPhoto} />}
      </Form.Item>
    </Form>
    </div>
  )
});

Company.displayName = "Company";

export default Company;
